import React, { useState } from 'react';
import {
  ComposableTable,
  getTextCellGenerator,
  getTextWithSubtextCellGenerator,
} from 'components/ComposableTable';
import { CoachRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingCoordinatorEditCoachSidebar from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorEditCoachSidebar';
import { useCoachingViewState } from 'coaching/components/CoachingView';
import Row from 'components/Row';
import ScrollLoadZone from 'components/ScrollLoadZone';
import TimezoneHelper from 'lib/timezones';

export type Props = {
  coaches: CoachRow[];
  loadingCoaches: boolean;
  hasNextPage?: boolean;
  onNextPage: () => Promise<void>;

  // we pass this into this component because we need to refetch the coach list
  // query with the exact same variables in order for the UI to update correctly
  // after calling the updateCoach mutation
  nameQuery: string;
};

const columnHeaders = () => {
  return [{ name: 'Name' }, { name: 'Status' }, { name: 'Timezone' }];
};

export default function CoachingCoordinatorCoachList(props: Props) {
  const { coaches, loadingCoaches, hasNextPage, onNextPage } = props;
  const [selectedCoach, setSelectedCoach] = useState<CoachRow | undefined>();
  const { setSidebar, closeSidebar } = useCoachingViewState();

  const columnGenerators = (): Array<(data: CoachRow) => React.ReactNode> => {
    return [
      getTextWithSubtextCellGenerator({
        text: 'name',
        subtext: 'uuid',
        textWithSubtextProps: {
          subtle: false,
        },
      }),
      getTextCellGenerator('status'),
      getTextCellGenerator(row => TimezoneHelper.convertToReadableShortCode(row.timezone ?? 'UTC')),
    ];
  };

  const onEditExistingCoachSidebar = (coach: CoachRow) => {
    const coachContainer = (
      <CoachingCoordinatorEditCoachSidebar
        onHide={closeSidebar}
        selectedCoach={coach}
        nameQuery={props.nameQuery}
      />
    );
    setSidebar(coachContainer);
  };

  return (
    <React.Fragment>
      <Row>
        <ComposableTable<CoachRow>
          rows={coaches}
          columnHeaders={columnHeaders()}
          columnGenerators={columnGenerators()}
          handleRowClick={(coach: CoachRow) => {
            setSelectedCoach(coach);
            onEditExistingCoachSidebar(coach);
          }}
          fixedHeader
          keyField="uuid"
          keysOfActiveRows={selectedCoach && [selectedCoach.uuid]}
        />
      </Row>
      {loadingCoaches && <CenteredLoadingIndicator />}
      {hasNextPage && <ScrollLoadZone onZoneActivated={onNextPage} />}
    </React.Fragment>
  );
}
